import React, { useState } from "react";
import { classNames } from "repoV2/utils/common/render/classNames";
import Head from "next/head";
import { RepeatedBreaks } from "@Modules/common/Spacers";
import dynamic from "next/dynamic";
import {
    EVENT_TYPE,
    CONTACT_KEYS,
    SELECTORS,
    useData,
    useDataRead,
    NEED_USER_INPUT_TO_PROCEED_KEY,
} from "@Utils";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TelegramIcon from "@mui/icons-material/Telegram";
import { useSelector } from "react-redux";
import { IEventPage, IHost } from "@Interfaces";
import ContactWidgetFloatModal from "@Modules/ContactWidgetFloatModal";
import useCompactStatusForEventPage from "@Modules/eventPage/utils/useCompactStatusForEventPage";
import { useEvaluatePaymentPrice } from "@Modules/eventPage/utils/useEvaluatePaymentPrice";
import { ICompactEventPageModule } from "./ICompactEventPageModule";
import { EventMedia, LoyaltyDiscountsPopup } from "..";
import { SelectTimezone } from "../../../common";
import styles from "../../event.module.scss";
import commonStyles from "../commonStyles.module.scss";
import RecentSalesPopup from "../RecentSalesPopup";
import RenewBookingForm from "../RenewBookingForm";
import CTAButtons from "../CTAButtons";
import EarlyBird from "../EarlyBird";
import TicketCount from "../TicketCount";
import ScheduleSelector from "../ScheduleSelector";
import ExpressInterest from "../ExpressInterest";
import { SidebarSectionDivider } from "../SidebarSectionDivider";
import { getHidePaymentSummary } from "../PaymentSummary/utils/getHidePaymentSummary";
import VariantSelector from "../VariantSelector/VariantSelector";
import CheckAvailability from "../CheckAvailability/CheckAvailability";
import usePaymentSummaryData from "../PaymentSummary/utils/usePaymentSummaryData";
import module_styles from "../../../common/ContactWidget/contactWidget.module.scss";
import LazyLoadedPaymentPlan from "../PaymentPlan/LazyLoadedPaymentPlan";
import { PaymentSummaryWithDiscount } from "../PaymentSummary/components/PaymentSummaryWithDiscount";
import { OfferingVariationSelector } from "../OfferingVariationSelector/OfferingVariationSelector";

const PlanDropdown = dynamic(() => import("../PlanDropdown"));

// Original dev's note:
// A brief on how this module has been built
// https://img.devrant.com/devrant/rant/r_3898730_rZ4yd.jpg

const CompactEventPage = ({
    hideSelf,
    showOpaqueBackdrop,
    eventData,
    eventMediaProps,
    planDropdownProps,
    disablePayment,
    isSoldOut,
    scheduleSelectorProps,
    recentSalesPopupProps,
    ctaButtonProps,
    ticketCountProps,
    paymentSummaryProps,
    contentHeaderProps,
    slots,
    eventType,
    // props calculated in the parent component
    discountProps,
    earlyBirdProps,
    expressInterestProps,
}: ICompactEventPageModule.IProps): JSX.Element => {
    const hidePaymentSummary = getHidePaymentSummary({
        disablePayment,
        eventData,
    });
    const hostData = useSelector(SELECTORS.hostData) || ({} as IHost.IHostData);
    const modalInfo = useSelector(SELECTORS.modalInfo);
    const communityAppData = useSelector(SELECTORS.communityAppData);
    const isTelegram = hostData.contact_mode === CONTACT_KEYS.Telegram;
    const { title } = contentHeaderProps;
    const { hideComponent, evaluatePaymentPriceProps } =
        usePaymentSummaryData(paymentSummaryProps);

    const {
        renderLeft: evaluatePricePerItemsLeft = <></>,
        renderRight: evaluatePricePerItemsRight = <></>,
    } = useEvaluatePaymentPrice(
        evaluatePaymentPriceProps
    ) as IEventPage.IEvaluatePaymentPriceResult;

    const [isOpen, setOpen] = useState<boolean>(false);
    const { data: hideContactWidget } = useData("hideContactWidget");
    const isCompactEventPage = useCompactStatusForEventPage();
    const isShowContactIcon =
        (
            ["none", "only_booked_user"] as Array<typeof hostData.contact_mode>
        ).includes(hostData.contact_mode) ||
        !hostData.contact_url ||
        modalInfo.isModalOpen ||
        hideContactWidget ||
        communityAppData ||
        !isCompactEventPage;

    const userInputInitiated = useDataRead(
        NEED_USER_INPUT_TO_PROCEED_KEY
    )?.initiated;

    return (
        <>
            <Head>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
            </Head>
            <div
                className={classNames(
                    styles.root,
                    // // To hide the base event page modal when another modal is openend.
                    // // Not for when event page is opened with a dedicated url
                    // hideSelf && isModalOpen && modalType ? styles.hide : "",

                    // If the eventpage is opened as a modal, add the isModal css class
                    hideSelf ? styles.isModal : "",
                    showOpaqueBackdrop && styles.opaqueBackdrop
                )}
            >
                <div
                    className={classNames(
                        styles.container,
                        styles.compactEventPageContainer
                    )}
                >
                    <div className={styles.sidebar}>
                        <EventMedia {...eventMediaProps} />
                        <div className="d-flex align-items-center justify-content-between mt-2">
                            {title ? (
                                <div className={styles.sectionBigTitle}>
                                    {title}
                                </div>
                            ) : null}
                            {!isShowContactIcon && (
                                <div
                                    className={`${
                                        isTelegram
                                            ? styles.telegram
                                            : styles.floatIcon
                                    }`}
                                    onClick={() => {
                                        setOpen(!isOpen);
                                    }}
                                >
                                    {isTelegram ? (
                                        <TelegramIcon fontSize="large" />
                                    ) : (
                                        <WhatsAppIcon fontSize="large" />
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            className={styles.paymentSummary}
                            style={{ display: "flex" }}
                        >
                            <EarlyBird {...earlyBirdProps} />

                            <TicketCount
                                {...ticketCountProps}
                                bottomSibling={<SidebarSectionDivider />}
                            />

                            <PlanDropdown {...planDropdownProps} />

                            {eventData.is_offering_variants_enabled &&
                                userInputInitiated && (
                                    <OfferingVariationSelector />
                                )}

                            {!isSoldOut && (
                                <SelectTimezone
                                    alwaysRefetch={{
                                        event: true,
                                        host: false,
                                    }}
                                />
                            )}

                            <RenewBookingForm />

                            <VariantSelector />

                            <LazyLoadedPaymentPlan
                                earlyBirdProps={earlyBirdProps}
                                paymentSummaryProps={paymentSummaryProps}
                            />

                            {/* Schedule Selector for each type of listing */}

                            <ScheduleSelector
                                {...scheduleSelectorProps}
                                titleClass={commonStyles.sidebarSectionTitle}
                            />
                            <SidebarSectionDivider />

                            <ExpressInterest
                                showOn="desktop"
                                {...expressInterestProps}
                            />

                            <CheckAvailability
                                disablePayment={disablePayment}
                            />
                            {eventType === EVENT_TYPE.APPOINTMENTS && (
                                <>
                                    <div className={styles.priceDesc}>
                                        {/* Incase hide_payment_summary check is true, 
                                            then we do not have to show this section */}
                                        {!hideComponent &&
                                            evaluatePricePerItemsLeft}

                                        <span className="flexGrow" />
                                        {/* Incase hide_payment_summary check is true, 
                                            then we do not have to show this section */}
                                        {!hideComponent &&
                                            evaluatePricePerItemsRight}
                                    </div>
                                    {hidePaymentSummary ? null : (
                                        <SidebarSectionDivider />
                                    )}
                                </>
                            )}

                            <PaymentSummaryWithDiscount
                                paymentSummaryProps={paymentSummaryProps}
                                eventData={eventData}
                                eventType={eventType}
                                slots={slots}
                                hideOfferPromotion
                                discountProps={discountProps}
                                offerPromotionProps={{}}
                            />
                            <RecentSalesPopup {...recentSalesPopupProps} />
                            {!disablePayment && (
                                <>
                                    {/* to avoid overlapping of above components by "BOOK A SPOT" button */}
                                    <RepeatedBreaks n={2} />
                                    <CTAButtons {...ctaButtonProps} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* cannot move `hideComponent` statement inside the component as open/close
                popup animation starts behaving weirdly */}
            {!discountProps.loyaltyDiscountsPopupProps.hideComponent ? (
                <LoyaltyDiscountsPopup
                    {...discountProps.loyaltyDiscountsPopupProps}
                />
            ) : null}
            <div
                className={classNames(
                    module_styles.root,
                    isTelegram && module_styles.telegram,
                    isOpen && module_styles.isOpen,
                    module_styles.isEventPage,
                    "pb-2"
                )}
            >
                <ContactWidgetFloatModal
                    isOpen={isOpen}
                    hostData={hostData}
                    setOpen={setOpen}
                    isTelegram={isTelegram}
                />
            </div>
        </>
    );
};

export default CompactEventPage;
// The goal should be to keep this page length < 1000 lines
